import { useQuery as useTanstackQuery, type QueryKey } from '@tanstack/vue-query'
import { computed } from 'vue'

import { useQuery } from '@/composables/api'
import type { components } from '@/types/_api'
import { isListResponse } from '@/types/_general'
import type { NovaCreditBalance } from '@/types/nova'
import { formatBankAccount } from '@/utils/format'
import { isNovaEnabled, NOVA_CREDIT_BALANCES_PATH, novaFetch } from '@/utils/novaApi'

/**
 * Overpayments the debtor can see in Mitt Amili (ADA-4345).
 *
 * Both filters are query-side on purpose. They are product rules about what is worth showing in
 * self-service, not a security boundary — the boundary is the `debtor`/`debtor_unit` filter the
 * api injects server-side from the token, which no client query can widen.
 */

/**
 * Self-service does not reach back before the launch date — except for overpayments that got the
 * overpayment notice letter (see the `$or` in useOverpayments). The date is the api's
 * `Config.OVERPAYMENT_NOTICE_FROM` (ADA-4476 B2, product decision 2026-09-08): letters and
 * self-service start from the same day, so no debtor is pointed at a list that hides their money.
 *
 * A Date, not a string: the api layer serializes Date objects in `where` to RFC 7231 via
 * `dateReplacer`, which is the format the api compares datetimes in. An ISO string silently
 * matches NOTHING — verified against the local api, where "2026-08-01" returned 0 of 6 records.
 */
export const EARLIEST_CREATED = new Date('2026-09-05T00:00:00Z')

/** Nova statuses in the ADA `status` vocabulary; legacy Nova statuses (null) have no place in the list. */
const NOVA_STATUS_TO_ADA: Record<NonNullable<NovaCreditBalance['status']>, string> = {
  unhandled: 'overpayment',
  approved: 'ready_for_settlement',
  paid_out: 'completed',
}

/**
 * Keeps the `['get', <path>, params]` shape so `invalidatePathPrefixes` reaches it; the Register
 * pins the path member to ada-api's OpenAPI paths, which Nova's hand-written contract is not.
 */
const NOVA_CREDIT_BALANCES_QUERY_KEY = ['get', NOVA_CREDIT_BALANCES_PATH, undefined] as unknown as QueryKey

export type OverpaymentState = 'to_handle' | 'pending' | 'denied' | 'handled' | 'paid_out' | 'returned'

/** The generated schema — the approval fields below are derived from it, not restated. */
type ApiOverpayment = components['schemas']['Finance-Overpayment']

/** The reason enum finance picks when denying a booking (ADA-4479 B10). */
export type DeniedReason = NonNullable<ApiOverpayment['approval_status_denied_reason']>

export interface Overpayment {
  _id: string
  _created: string
  amount?: number
  currency?: string
  status?: string
  /** Set on Nova credit balances; ADA records carry no source. */
  source?: 'nova'
  /** Nova row id, the key for approving payout */
  minibank_nr?: number
  /** Nova's stored payout method for the debtor identity (one of giro or bank account) */
  payout_method?: { giro?: string; bank_name?: string; bank_account?: string }
  approval_status?: ApiOverpayment['approval_status']
  approval_status_denied_reason?: ApiOverpayment['approval_status_denied_reason']
  booking_date?: string
  creditor_name?: string
  /**
   * Set on the overpayment that holds money the bank returned after a payout. Hand-written until
   * codegen picks the field up from the api schema (ADA-4475).
   */
  returned_refund?: boolean | null
  /** The overpayment whose refund the bank returned; set together with `returned_refund`. */
  finance_overpayment_returned?: string | null
  /** The overpayment this remainder was carved out of when an earlier post was placed. */
  finance_overpayment?: ApiOverpayment['finance_overpayment']
  /** The unplaced transaction a placement left this post from — an id the debtor cannot open. */
  finance_unplaced_transaction?: string | null
  settlement?: {
    /** `ocr_number` is the /ocr-numbers objectid — the OCR string needs a lookup. */
    ocr_numbers?: { ocr_number?: string; amount?: number }[]
    payout_details?: {
      amount_for_payout?: number
      name?: string
      payout_reference?: string
      bank_details?: {
        bank_account_number?: { clearing_number?: string; account_number?: string }
        bankgiro?: string
        plusgiro?: string
      }
    }
  }
}

/** An overpayment that holds money the bank returned after a payout. */
export function isReturnedRefund(overpayment: Overpayment): boolean {
  return overpayment.returned_refund === true
}

/**
 * The overpayment holding the money the bank returned from `overpayment`'s payout. The api writes
 * nothing on the original when a payout comes back, so the child's link is the only trace of the return.
 */
export function findReturnedChild(overpayments: Overpayment[], overpayment: Overpayment): Overpayment | undefined {
  return overpayments.find(
    (candidate) => candidate.finance_overpayment_returned === overpayment._id && isReturnedRefund(candidate)
  )
}

/** The overpayment whose payout came back as `overpayment`, when it is in the debtor's list. */
export function findOrigin(overpayments: Overpayment[], overpayment: Overpayment): Overpayment | undefined {
  if (overpayment.finance_overpayment_returned == null) return undefined
  return overpayments.find((candidate) => candidate._id === overpayment.finance_overpayment_returned)
}

export type PayoutInstrument = 'bank_account' | 'bankgiro' | 'plusgiro'

/**
 * The instrument a settled payout went to, with the number as the debtor would write it. Null when
 * the settlement carries none (a staff-settled payout may not).
 */
export function payoutInstrument(overpayment: Overpayment): { method: PayoutInstrument; number: string } | null {
  const bankDetails = overpayment.settlement?.payout_details?.bank_details
  if (bankDetails?.bank_account_number != null) {
    const { clearing_number: clearing, account_number: account } = bankDetails.bank_account_number
    return { method: 'bank_account', number: formatBankAccount(clearing ?? '', account ?? '') }
  }
  if (bankDetails?.bankgiro != null) return { method: 'bankgiro', number: bankDetails.bankgiro }
  if (bankDetails?.plusgiro != null) return { method: 'plusgiro', number: bankDetails.plusgiro }
  return null
}

/**
 * The state a debtor sees, derived from `status` + `approval_status` (ADA-4479 step 3.12) — no
 * field of its own carries it.
 *
 * `returned` is derived from the whole list: the api writes nothing on a paid-out original whose
 * payout came back, so the state is read off the returned-refund child that points at it (ADA-4475).
 *
 * A booking now always parks in `approval_status: pending` with the status still `overpayment`
 * (the book_settlement contract), so "pending" replaces the old "handled" reading for booked
 * records. `denied` means finance rejected the booking — the debtor can redo it from the start.
 * `handled` remains for `ready_for_settlement` (approved, queued for settlement) and for `error`:
 * a technical failure is not the debtor's problem to see, the record is handled from their point
 * of view and DSS picks it up manually.
 */
export function overpaymentState(overpayment: Overpayment, overpayments: Overpayment[]): OverpaymentState {
  if (overpayment.status === 'completed') {
    return findReturnedChild(overpayments, overpayment) == null ? 'paid_out' : 'returned'
  }
  if (overpayment.approval_status === 'denied') return 'denied'
  if (overpayment.approval_status === 'pending') return 'pending'
  if (overpayment.status === 'ready_for_settlement' || overpayment.status === 'error') return 'handled'
  return 'to_handle'
}

/** States the debtor can act on: an unbooked overpayment, or a denied booking they can redo. */
function toHandleRank(state: OverpaymentState): number {
  if (state === 'denied') return 0
  if (state === 'to_handle') return 1
  return 2
}

export function isActionableState(state: OverpaymentState): boolean {
  return state === 'to_handle' || state === 'denied'
}

/** Both states of a completed record: paid out, or paid out and returned by the bank. */
export function isPaidOutState(state: OverpaymentState): boolean {
  return state === 'paid_out' || state === 'returned'
}

/**
 * The amount to show. Paid-out records (returned or not) show what was actually paid, which can
 * differ from the overpayment's own amount when staff split it across several placements.
 */
export function overpaymentAmount(overpayment: Overpayment): number | undefined {
  if (overpayment.status === 'completed') {
    return overpayment.settlement?.payout_details?.amount_for_payout ?? overpayment.amount
  }
  return overpayment.amount
}

/**
 * A Nova credit balance in the ADA `Overpayment` shape, so every state/amount rule above applies
 * unchanged. `name`/`identification` are deliberately not copied — the list never renders them.
 * Returns null for rows in a legacy Nova status, which are dropped.
 */
export function mapNovaCreditBalance(row: NovaCreditBalance): Overpayment | null {
  if (row.status == null) return null
  // Nova pads with spaces, so emptiness is tested on the trimmed value.
  const giro = row.giro.trim()
  const bankName = row.bank_name.trim()
  const bankAccount = row.bank_account.trim()
  let payoutMethod: Overpayment['payout_method']
  if (giro !== '') {
    payoutMethod = { giro }
  } else if (bankAccount !== '') {
    payoutMethod = { bank_name: bankName, bank_account: bankAccount }
  }
  return {
    _id: `nova-${row.minibank_nr}`,
    _created: row.date,
    amount: row.amount,
    currency: row.currency,
    status: NOVA_STATUS_TO_ADA[row.status],
    source: 'nova',
    minibank_nr: row.minibank_nr,
    ...(payoutMethod != null && { payout_method: payoutMethod }),
  }
}

export function useOverpayments() {
  const query = useQuery('/finance--overpayments', {
    query: {
      where: {
        // The api stamps this flag at settle time from the settlement system-config thresholds
        // (overpayment_book_as_income_below + the state-dependent handling fee), so it IS the
        // api's verdict on whether the record is the debtor's to handle. The client-side floor
        // it replaces (amount >= 399) duplicated those thresholds and drifted from them — the
        // fee is not claimed in every case state and the config can change under the UI (#61).
        // $ne rather than $eq false so a record without the field is not offered by accident.
        amount_booked_as_income: { $ne: true },
        // Created since the launch date, OR notified by letter: finance can send the overpayment
        // notice manually for an older record, and the letter points the debtor here (ADA-4476
        // B13). The api grants customer users read on `overpayment_notice_sent` for exactly this
        // where clause — a filter on an unreadable attribute is refused outright.
        $or: [{ _created: { $gte: EARLIEST_CREATED } }, { overpayment_notice_sent: true }],
      },
      sort: '-_created',
    },
    getAll: true,
  })

  // Nova rows come from nova-ada-api directly (same token, see novaApi). A failed call is an
  // error here, not data, so it is retried on the next mount/invalidation instead of being cached
  // as fresh for staleTime; the global error toast is off because the view shows its own banner.
  const novaQuery = useTanstackQuery({
    queryKey: NOVA_CREDIT_BALANCES_QUERY_KEY,
    queryFn: async () => {
      const response = await novaFetch<{ _items: NovaCreditBalance[] }>(NOVA_CREDIT_BALANCES_PATH)
      if (!response.ok || response.data == null) {
        throw new Error(`Nova credit balances request failed with ${response.status}`)
      }
      // Mapped before caching so the masked `name`/`identification` never enter the query cache.
      return response.data._items
        .map(mapNovaCreditBalance)
        .filter((overpayment): overpayment is Overpayment => overpayment != null)
    },
    enabled: isNovaEnabled(),
    retry: false,
    meta: { notifyOnError: false },
  })

  const overpayments = computed<Overpayment[]>(() => {
    const data = query.data.value
    const adaItems = isListResponse(data) ? (data._items as unknown as Overpayment[]) : []
    return [...adaItems, ...(novaQuery.data.value ?? [])].sort(
      (a, b) => Date.parse(b._created) - Date.parse(a._created)
    )
  })

  /** Nova was asked and did not answer usably — the view tells the debtor some records may be missing. */
  const novaUnavailable = computed(() => novaQuery.isError.value)

  /**
   * Both sources have answered (or Nova is off/failed), so the counts below are final for this load.
   * One-shot consumers such as the home nudge wait for this; a partial count is not worth announcing.
   */
  const isSettled = computed(
    () => query.isFetched.value && (!isNovaEnabled() || novaQuery.isFetched.value || novaQuery.isError.value)
  )

  // Urgency order within the tab: denied bookings first (the debtor has to redo them), then records
  // awaiting a first decision, then records merely being processed. Newest first within each group,
  // since `overpayments` is already date-sorted and the sort is stable.
  const getState = (overpayment: Overpayment) => overpaymentState(overpayment, overpayments.value)
  const toHandle = computed(() =>
    overpayments.value
      .filter((overpayment) => !isPaidOutState(getState(overpayment)))
      .sort((a, b) => toHandleRank(getState(a)) - toHandleRank(getState(b)))
  )
  const paidOut = computed(() => overpayments.value.filter((overpayment) => isPaidOutState(getState(overpayment))))

  /**
   * Sum of what Amili still owes the debtor, i.e. everything not yet paid out (ADA-4345 step 2.14).
   *
   * Currently unused: the Home row "Varav X ska återbetalas till dig" was dropped (ADA-4345 beslut
   * #44) because it disagreed with the nudge's count. Kept because the umbrella requirement — show
   * money owed to the debtor OUTSIDE the payable total, never folded into "Totalt att betala" — is
   * still an open PO question.
   */
  const overpaymentsToRepay = computed(() =>
    toHandle.value.reduce((sum, overpayment) => sum + (overpayment.amount ?? 0), 0)
  )

  /** Drives the navigation badge: only records the debtor can still act on (incl. denied). */
  const unhandledCount = computed(
    () => overpayments.value.filter((overpayment) => isActionableState(getState(overpayment))).length
  )

  return {
    query,
    overpayments,
    novaUnavailable,
    toHandle,
    paidOut,
    overpaymentsToRepay,
    unhandledCount,
    isSettled,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
